import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { api, ApiError, type GroupChangeMode, type GroupChangePreview } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { rrRebuildNotice } from "../lib/rrRebuild";
import RoundRobinPreviewJornadas from "./RoundRobinPreviewJornadas";

/** Grupo al que se puede pasar el jugador, con sus miembros en el orden de la hoja. */
export interface RoundRobinChangeGroupTarget {
  id: number;
  name: string;
  members: Array<{ id: number; name: string }>;
}

interface RoundRobinChangeGroupDialogProps {
  token: string;
  tournamentId: number;
  participant: { id: number; name: string };
  fromGroup: { id: number; name: string };
  /** Grupos activos de la misma categoría, sin el de origen. */
  targetGroups: RoundRobinChangeGroupTarget[];
  /** Ya se guardó: `toGroupId` es el grupo destino (el de origen es
   * `fromGroup`); `notice` = «Se rehicieron los partidos de … y quedaron sin
   * programar…» para mostrarlo en la pantalla (o null). */
  onDone: (result: { toGroupId: number; notice: string | null }) => void;
  onClose: () => void;
  /** Aviso extra para la confirmación. Ya no lo usa ninguna pantalla: el
   * resumen del backend trae la frase del acta del sorteo. Se conserva
   * opcional por compatibilidad. */
  confirmNotice?: string | null;
}

const LOCK_FALLBACK = "La categoría ya empezó: ya no se pueden mover jugadores ni cambiar el orden.";

/** Lo que se puede enfocar dentro de la hoja (para no salir con Tab). */
const FOCUSABLE =
  'button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])';

/** Clase en <body> mientras la hoja está abierta: sube la confirmación
 * encima de la hoja (ver styles.css). */
const BODY_OPEN_CLASS = "rr-change-sheet-open";

/**
 * «Cambiar de grupo…» en el Round Robin (Eduardo 2026-10-01), antes de que
 * empiece la categoría. Dos caminos:
 * - «Intercambiar con…» (sugerido): cada quien toma el lugar del otro.
 * - «Mover solo»: pasa al último lugar del grupo destino.
 * En los dos se rehacen los partidos de los dos grupos y los pendientes
 * quedan sin programar (Eduardo 2026-10-13: «si hay cambio de grupo
 * entonces desprogramar los juegos en automático»). La revisión avisa
 * cuántos tenían fecha, hora o cancha.
 * Primero «Revisar cambios» (el backend arma el resumen) y solo con esa
 * revisión vigente se puede confirmar. Cambiar grupo, modo o con quién
 * intercambiar descarta la revisión; el motivo no (no cambia el plan).
 */
export default function RoundRobinChangeGroupDialog({
  token,
  tournamentId,
  participant,
  fromGroup,
  targetGroups,
  onDone,
  onClose,
  confirmNotice = null,
}: RoundRobinChangeGroupDialogProps) {
  const { askConfirm } = useAbortableConfirm([tournamentId, participant.id]);
  const ids = useId();
  const [toGroupId, setToGroupId] = useState<number | "">(targetGroups[0]?.id ?? "");
  const [mode, setMode] = useState<GroupChangeMode>("swap");
  const [swapWithId, setSwapWithId] = useState<number | "">("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<GroupChangePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"preview" | "apply" | null>(null);
  const firstFieldRef = useRef<HTMLSelectElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const summaryRef = useRef<HTMLParagraphElement | null>(null);
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  // Mientras la confirmación está abierta, su Esc es de ella, no del diálogo.
  const confirmingRef = useRef(false);
  const busyRef = useRef(busy);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const targetGroup = targetGroups.find((group) => group.id === toGroupId) ?? null;

  // Al abrir: foco en el primer campo, la página de atrás no se desplaza y
  // la confirmación queda encima de la hoja. Al cerrar, el foco regresa a
  // donde estaba (el botón «Cambiar de grupo…» del renglón).
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add(BODY_OPEN_CLASS);
    firstFieldRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove(BODY_OPEN_CLASS);
      if (previouslyFocused && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || confirmingRef.current) return;
      if (event.key === "Tab") {
        // El foco no sale de la hoja (aria-modal): de la última opción
        // regresa a la primera y al revés.
        const panel = panelRef.current;
        if (!panel) return;
        const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!panel.contains(active)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key !== "Escape") return;
      if (busyRef.current === "apply") return;
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // «Revisar cambios» se deshabilita mientras revisa y el foco se perdería:
  // al llegar la revisión (o el error) el foco va a ese texto.
  useEffect(() => {
    if (preview) summaryRef.current?.focus();
  }, [preview]);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  function requestClose() {
    if (busy === "apply") return;
    onClose();
  }

  /** Cualquier cambio del formulario invalida la revisión. */
  function clearPreview() {
    setPreview(null);
    setError(null);
  }

  const canReview =
    busy === null && toGroupId !== "" && (mode === "move" || swapWithId !== "");
  const canApply = busy === null && preview !== null && !preview.locked && Boolean(preview.plan_token);

  async function handleReview() {
    // `canReview` ya garantiza que hay grupo destino (TS lo estrecha a número).
    if (!canReview) return;
    setBusy("preview");
    setPreview(null);
    setError(null);
    try {
      const response = await api.groups.changeGroupPreview(token, tournamentId, participant.id, {
        to_group_id: toGroupId,
        mode,
        swap_with_participant_id: mode === "swap" && swapWithId !== "" ? swapWithId : null,
        // El grupo que se ve en pantalla: si otro ya lo cambió, el backend
        // responde que se recargue (no intercambia desde otro grupo).
        from_group_id: fromGroup.id,
      });
      setPreview(response.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo revisar el cambio." : "Error de red. Intenta otra vez.");
    } finally {
      setBusy(null);
    }
  }

  async function handleApply() {
    if (!canApply || !preview?.plan_token || toGroupId === "") return;
    const destructive =
      (preview.counts.lost_schedule ?? 0) > 0
      || (mode === "move" && (preview.counts.removed > 0 || preview.counts.created_without_schedule > 0));
    const message = confirmNotice ? `${preview.summary}\n\n${confirmNotice}` : preview.summary;
    confirmingRef.current = true;
    let accepted = false;
    try {
      accepted = await askConfirm(message, {
        acceptLabel: mode === "swap" ? "Sí, intercambiar" : "Sí, mover",
        tone: destructive ? "danger" : "default",
      });
    } finally {
      confirmingRef.current = false;
    }
    if (!accepted) return;
    setBusy("apply");
    setError(null);
    try {
      const response = await api.groups.changeGroup(token, tournamentId, participant.id, {
        to_group_id: toGroupId,
        mode,
        swap_with_participant_id: mode === "swap" && swapWithId !== "" ? swapWithId : undefined,
        reason: reason.trim() || null,
        plan_token: preview.plan_token,
        from_group_id: fromGroup.id,
      });
      setBusy(null);
      onDone({ toGroupId, notice: rrRebuildNotice(response.data?.rr_rebuild) });
    } catch (err) {
      setBusy(null);
      if (err instanceof ApiError) {
        // Rechazo del negocio (revisión vieja, categoría ya empezada, lista
        // distinta…): hay que volver a revisar antes de confirmar.
        setPreview(null);
        setError(err.message || "No se pudo cambiar de grupo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    }
  }

  const withoutSchedule = preview && !preview.locked && mode === "move" ? preview.counts.created_without_schedule : 0;
  // Pendientes que ya tenían fecha, hora o cancha y se quedan sin programar.
  const losesSchedule = preview && !preview.locked ? preview.counts.unscheduled ?? 0 : 0;

  return createPortal(
    <div className="rr-change-sheet" role="dialog" aria-modal="true" aria-labelledby={`${ids}-title`}>
      <div className="rr-change-sheet__backdrop" onClick={requestClose} />
      <div className="rr-change-sheet__panel" ref={panelRef}>
        <header className="rr-change-sheet__header">
          <div>
            <span className="player-pro-eyebrow">Cambiar de grupo</span>
            <strong id={`${ids}-title`}>
              {participant.name} · {fromGroup.name}
            </strong>
          </div>
          <button
            type="button"
            className="rr-change-sheet__close"
            onClick={requestClose}
            disabled={busy === "apply"}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <div className="rr-change-sheet__body" data-no-toast>
          {targetGroups.length === 0 ? (
            <p className="muted">No hay otro grupo activo en esta categoría.</p>
          ) : (
            <>
              <label className="rr-change-sheet__field">
                <span>Grupo destino</span>
                <select
                  ref={firstFieldRef}
                  value={toGroupId}
                  disabled={busy !== null}
                  onChange={(event) => {
                    setToGroupId(event.target.value ? Number(event.target.value) : "");
                    setSwapWithId("");
                    clearPreview();
                  }}
                >
                  {targetGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </label>

              <fieldset className="rr-change-sheet__modes" disabled={busy !== null}>
                <legend>¿Cómo se cambia?</legend>
                <label className={`rr-change-sheet__mode${mode === "swap" ? " is-active" : ""}`}>
                  <input
                    type="radio"
                    name={`${ids}-mode`}
                    value="swap"
                    checked={mode === "swap"}
                    onChange={() => {
                      setMode("swap");
                      clearPreview();
                    }}
                  />
                  <span>
                    <strong>Intercambiar con…</strong>
                    <small>
                      Cada quien toma el lugar del otro. Se rehacen los partidos de los dos grupos y los pendientes
                      quedan sin programar.
                    </small>
                  </span>
                </label>
                {mode === "swap" ? (
                  <label className="rr-change-sheet__field rr-change-sheet__field--nested">
                    <span>Intercambiar con</span>
                    <select
                      value={swapWithId}
                      onChange={(event) => {
                        setSwapWithId(event.target.value ? Number(event.target.value) : "");
                        clearPreview();
                      }}
                    >
                      <option value="">Elige a un jugador del {targetGroup?.name ?? "grupo destino"}…</option>
                      {(targetGroup?.members ?? []).map((member, index) => (
                        <option key={member.id} value={member.id}>
                          {index + 1}. {member.name}
                        </option>
                      ))}
                    </select>
                    {targetGroup && targetGroup.members.length === 0 ? (
                      <small className="muted">Ese grupo no tiene jugadores: usa «Mover solo».</small>
                    ) : null}
                  </label>
                ) : null}
                <label className={`rr-change-sheet__mode${mode === "move" ? " is-active" : ""}`}>
                  <input
                    type="radio"
                    name={`${ids}-mode`}
                    value="move"
                    checked={mode === "move"}
                    onChange={() => {
                      setMode("move");
                      clearPreview();
                    }}
                  />
                  <span>
                    <strong>Mover solo</strong>
                    <small>
                      Pasa al último lugar del grupo destino. Se rehacen los partidos de los dos grupos y los
                      pendientes quedan sin programar.
                    </small>
                  </span>
                </label>
              </fieldset>

              <label className="rr-change-sheet__field">
                <span>Motivo (opcional, queda en el historial)</span>
                <textarea
                  rows={2}
                  maxLength={500}
                  value={reason}
                  disabled={busy !== null}
                  // El motivo solo va al historial: no cambia el plan, así que
                  // escribirlo NO descarta la revisión.
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>

              <button type="button" className="btn btn--ghost" onClick={() => void handleReview()} disabled={!canReview}>
                {busy === "preview" ? "Revisando…" : "Revisar cambios"}
              </button>
            </>
          )}

          {error ? (
            <p role="alert" className="form-error" ref={errorRef} tabIndex={-1}>
              {error}
            </p>
          ) : null}

          {preview ? (
            <div className="rr-change-sheet__preview">
              {preview.locked ? (
                <p className="field-warning" ref={summaryRef} tabIndex={-1}>
                  {preview.lock?.message || LOCK_FALLBACK}
                </p>
              ) : (
                <>
                  <p className="rr-change-sheet__summary" ref={summaryRef} tabIndex={-1}>
                    {preview.summary}
                  </p>
                  {preview.warnings.map((warning) => (
                    <p key={warning} className="field-warning">
                      {warning}
                    </p>
                  ))}
                  {losesSchedule > 0 ? (
                    <p className="rr-change-sheet__highlight">
                      {losesSchedule === 1
                        ? "1 partido que ya tenía fecha, hora o cancha quedará sin programar."
                        : `${losesSchedule} partidos que ya tenían fecha, hora o cancha quedarán sin programar.`}
                    </p>
                  ) : withoutSchedule > 0 ? (
                    <p className="rr-change-sheet__highlight">
                      {withoutSchedule === 1
                        ? "1 partido quedará sin horario."
                        : `${withoutSchedule} partidos quedarán sin horario.`}
                    </p>
                  ) : null}
                  {(preview.groups ?? []).map((group) => (
                    <details key={group.group_id} className="rr-change-sheet__group">
                      <summary>{group.group_name}</summary>
                      <RoundRobinPreviewJornadas jornadas={group.jornadas} />
                    </details>
                  ))}
                </>
              )}
            </div>
          ) : null}
        </div>

        <footer className="rr-change-sheet__footer">
          <button type="button" className="btn btn--ghost" onClick={requestClose} disabled={busy === "apply"}>
            No, regresar
          </button>
          <button type="button" className="btn" onClick={() => void handleApply()} disabled={!canApply}>
            {busy === "apply" ? "Guardando…" : "Cambiar de grupo"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
